import type { ChangeEvent } from "react";
import { OPTION_LISTS } from "../../config/fieldConfig/optionLists";
import type { FieldConfig, SelectOption } from "../../config/fieldConfig/types";
import FieldError from "../FieldError";
import { DatePicker } from "../ui/date-picker";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { FileUploadField } from "./FileUploadField";

interface FormFieldProps {
	config: FieldConfig;
	value: unknown;
	onChange: (fieldId: string, value: unknown) => void;
	onBlur?: (fieldId: string) => void;
	error?: string;
	disabled?: boolean;
	locale?: string;
}

function resolveOptions(optionsConfig: FieldConfig["options"]): SelectOption[] {
	if (!optionsConfig) return [];
	if (typeof optionsConfig === "string") {
		return OPTION_LISTS[optionsConfig] ?? [];
	}
	return optionsConfig;
}

function getLabel(config: FieldConfig, locale = "en"): string {
	return config.label[locale] ?? config.label.en ?? config.fieldId;
}

function getPlaceholder(config: FieldConfig, locale = "en"): string {
	return config.placeholder?.[locale] ?? config.placeholder?.en ?? "";
}

function getHelpText(config: FieldConfig, locale = "en"): string | undefined {
	return config.helpText?.[locale] ?? config.helpText?.en;
}

export function FormField({
	config,
	value,
	onChange,
	onBlur,
	error,
	disabled = false,
	locale = "en",
}: FormFieldProps) {
	const fieldId = config.fieldId;
	const fieldLabel = getLabel(config, locale);
	const fieldPlaceholder = getPlaceholder(config, locale);
	const fieldHelp = getHelpText(config, locale);

	const handleChange = (newValue: unknown) => onChange(fieldId, newValue);
	const handleBlur = () => onBlur?.(fieldId);

	const describedBy = error
		? `${fieldId}-error`
		: fieldHelp
			? `${fieldId}-help`
			: undefined;

	const RequiredMark = config.required ? (
		<span aria-hidden="true" className="ml-0.5 text-(--color-error-500)">
			*
		</span>
	) : null;

	const HelpText = fieldHelp ? (
		<p
			id={`${fieldId}-help`}
			className="text-(--color-muted-foreground) text-xs"
		>
			{fieldHelp}
		</p>
	) : null;

	const ErrorEl = error ? (
		<FieldError id={`${fieldId}-error`} message={error} />
	) : null;

	// Boolean: bordered switch + label row
	if (config.type === "boolean") {
		return (
			<div className="space-y-1.5">
				<div className="flex items-center gap-3 rounded-(--radius-sm) border border-(--color-border) p-3">
					<Switch
						id={fieldId}
						checked={(value as boolean) ?? false}
						onCheckedChange={(c) => handleChange(c)}
						disabled={disabled}
						aria-invalid={!!error}
						aria-describedby={describedBy}
					/>
					<Label htmlFor={fieldId} className="cursor-pointer font-normal">
						{fieldLabel}
						{RequiredMark}
					</Label>
				</div>
				{HelpText}
				{ErrorEl}
			</div>
		);
	}

	// Select: Radix UI controlled select
	if (config.type === "select") {
		return (
			<div className="space-y-1.5">
				<Label htmlFor={fieldId}>
					{fieldLabel}
					{RequiredMark}
				</Label>
				<Select
					value={(value as string) ?? ""}
					onValueChange={(v) => handleChange(v)}
					disabled={disabled}
				>
					<SelectTrigger
						id={fieldId}
						aria-invalid={!!error}
						aria-describedby={describedBy}
						onBlur={handleBlur}
					>
						<SelectValue placeholder="Select…" />
					</SelectTrigger>
					<SelectContent>
						{resolveOptions(config.options).map((opt) => (
							<SelectItem key={opt.value} value={opt.value}>
								{opt.label[locale] ?? opt.label.en}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{HelpText}
				{ErrorEl}
			</div>
		);
	}

	// Textarea
	if (config.type === "textarea") {
		return (
			<div className="space-y-1.5">
				<Label htmlFor={fieldId}>
					{fieldLabel}
					{RequiredMark}
				</Label>
				<Textarea
					id={fieldId}
					name={fieldId}
					disabled={disabled}
					onBlur={handleBlur}
					aria-invalid={!!error}
					aria-describedby={describedBy}
					value={(value as string) ?? ""}
					placeholder={fieldPlaceholder}
					onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
						handleChange(e.target.value)
					}
					rows={4}
				/>
				{HelpText}
				{ErrorEl}
			</div>
		);
	}

	// File input — uploads to S3 via FileUploadField
	if (config.type === "file") {
		return (
			<div className="space-y-1.5">
				<Label htmlFor={fieldId}>
					{fieldLabel}
					{RequiredMark}
				</Label>
				<FileUploadField
					compact
					id={fieldId}
					label={fieldLabel}
					required={config.required}
					value={value as string | undefined}
					onUpload={(key) => handleChange(key)}
					onRemove={() => handleChange(undefined)}
				/>
				{HelpText}
				{ErrorEl}
			</div>
		);
	}

	// Date: calendar popover picker
	if (config.type === "date") {
		return (
			<div className="space-y-1.5">
				<Label htmlFor={fieldId}>
					{fieldLabel}
					{RequiredMark}
				</Label>
				<DatePicker
					id={fieldId}
					value={(value as string) ?? ""}
					onChange={(val) => handleChange(val)}
					disabled={disabled}
				/>
				{HelpText}
				{ErrorEl}
			</div>
		);
	}

	// All remaining input types: text, email, url, phone, number
	const inputType =
		config.type === "phone"
			? "tel"
			: config.type === "number"
				? "number"
				: config.type === "email"
					? "email"
					: config.type === "url"
						? "url"
						: "text";

	return (
		<div className="space-y-1.5">
			<Label htmlFor={fieldId}>
				{fieldLabel}
				{RequiredMark}
			</Label>
			<Input
				id={fieldId}
				name={fieldId}
				type={inputType}
				disabled={disabled}
				onBlur={handleBlur}
				aria-invalid={!!error}
				aria-describedby={describedBy}
				value={(value as string | number) ?? ""}
				placeholder={fieldPlaceholder}
				onChange={(e: ChangeEvent<HTMLInputElement>) => {
					if (config.type === "number") {
						handleChange(
							e.target.value === "" ? undefined : Number(e.target.value),
						);
					} else {
						handleChange(e.target.value);
					}
				}}
			/>
			{HelpText}
			{ErrorEl}
		</div>
	);
}
